import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";

export default function ResidentProfilePhoto({
  src,
  name = "Resident",
  onChange,
  onRemove,
  editable = true,
  loading = false,
  helperText = "Select a clear resident profile photo.",
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraPreview, setCameraPreview] = useState("");
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const uploadInputRef = useRef(null);

  useEffect(() => setImageFailed(false), [src]);

  useEffect(() => {
    const stopCamera = () => {
      cameraStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      cameraStreamRef.current = null;
      if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    };
    if (!cameraOpen) {
      stopCamera();
      return undefined;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser does not expose camera access.");
      return undefined;
    }
    let active = true;
    setCameraLoading(true);
    setCameraError("");
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play?.().catch(() => {});
        }
      })
      .catch((error) => active && setCameraError(error?.message || "Unable to open the camera."))
      .finally(() => active && setCameraLoading(false));
    return () => {
      active = false;
      stopCamera();
    };
  }, [cameraOpen]);

  useEffect(() => () => {
    if (cameraPreview) URL.revokeObjectURL(cameraPreview);
  }, [cameraPreview]);

  const hasImage = Boolean(src) && !imageFailed;

  return (
    <div className="resident-profile-photo-tile">
      <span className="resident-profile-photo-label">Profile Photo</span>
      <div className="resident-profile-photo-avatar-wrap">
        <button
          type="button"
          className="resident-profile-photo-avatar"
          aria-label={hasImage ? `View ${name}'s profile photo` : "No resident profile photo"}
          disabled={!hasImage}
          onClick={() => hasImage && setPreviewOpen(true)}
        >
          {hasImage ? <img src={src} alt={`${name} profile`} onError={() => setImageFailed(true)} /> : <i className="ti ti-user" />}
        </button>
        {editable ? (
          <button
            className="resident-profile-photo-edit"
            type="button"
            aria-label="Update resident profile photo"
            title="Update profile photo"
            disabled={loading}
            onClick={() => setActionsOpen(true)}
          >
            <i className={`ti ${loading ? "ti-loader-2" : "ti-pencil"}`} />
          </button>
        ) : null}
      </div>
      {helperText ? <small>{helperText}</small> : null}
      {editable && hasImage && onRemove ? (
        <button className="resident-profile-photo-delete" type="button" onClick={onRemove}>
          <i className="ti ti-trash" /> Remove photo
        </button>
      ) : null}
      <Modal
        centered
        footer={null}
        open={previewOpen}
        title={`${name} - Profile Photo`}
        width={560}
        onCancel={() => setPreviewOpen(false)}
      >
        <div className="resident-profile-photo-modal">
          {hasImage ? <img src={src} alt={`${name} profile preview`} /> : null}
        </div>
      </Modal>
      <Modal
        centered
        footer={null}
        open={actionsOpen}
        title="Update Profile Photo"
        width={420}
        onCancel={() => setActionsOpen(false)}
      >
        <div className="resident-profile-photo-actions">
          <button type="button" className="take-photo" onClick={() => { setActionsOpen(false); setCameraPreview(""); setCameraOpen(true); }}>
            <i className="ti ti-camera" />
            <span><strong>Take Photo</strong><small>Use this device's camera.</small></span>
          </button>
          <button type="button" className="upload-photo" onClick={() => uploadInputRef.current?.click()}>
            <i className="ti ti-photo-up" />
            <span><strong>Upload Photo</strong><small>Choose an image from this device.</small></span>
          </button>
        </div>
        <input
          ref={uploadInputRef}
          className="resident-profile-photo-file-input"
          accept="image/*,.heic,.heif"
          type="file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setActionsOpen(false);
            onChange?.(file);
          }}
        />
      </Modal>
      <Modal
        centered
        footer={null}
        open={cameraOpen}
        title="Take Profile Photo"
        width={620}
        onCancel={() => { setCameraOpen(false); setCameraPreview(""); }}
      >
        <div className="resident-profile-camera">
          <div className="resident-profile-camera-stage">
            <video className={cameraPreview ? "is-hidden" : ""} ref={cameraVideoRef} autoPlay playsInline muted aria-label="Selfie camera preview" />
            {cameraPreview ? <img src={cameraPreview} alt="Captured resident profile preview" /> : null}
            {cameraLoading ? <span>Opening camera...</span> : null}
            {cameraError ? <p className="form-error">{cameraError}</p> : null}
          </div>
          <div className="resident-profile-camera-actions">
            {cameraPreview ? (
              <>
                <button className="btn secondary" type="button" onClick={() => { URL.revokeObjectURL(cameraPreview); setCameraPreview(""); }}><i className="ti ti-refresh" /> Retake</button>
                <button
                  className="btn primary"
                  type="button"
                  onClick={async () => {
                    const response = await fetch(cameraPreview);
                    const blob = await response.blob();
                    const file = new File([blob], `resident-profile-${Date.now()}.jpg`, { type: "image/jpeg" });
                    setCameraOpen(false);
                    setCameraPreview("");
                    onChange?.(file);
                  }}
                ><i className="ti ti-check" /> Use Photo</button>
              </>
            ) : (
              <button
                className="btn primary"
                type="button"
                disabled={cameraLoading || Boolean(cameraError)}
                onClick={() => {
                  const video = cameraVideoRef.current;
                  if (!video?.videoWidth || !video?.videoHeight) {
                    setCameraError("Camera is still starting. Try again in a moment.");
                    return;
                  }
                  const canvas = document.createElement("canvas");
                  const shortestSide = Math.min(video.videoWidth, video.videoHeight);
                  const cropSize = Math.round(shortestSide * 0.74);
                  const sourceX = Math.max(0, Math.round((video.videoWidth - cropSize) / 2));
                  const sourceY = Math.max(0, Math.round((video.videoHeight - cropSize) / 2));
                  canvas.width = 900;
                  canvas.height = 900;
                  const context = canvas.getContext("2d");
                  if (!context) {
                    setCameraError("Unable to take a camera photo in this browser.");
                    return;
                  }
                  context.translate(canvas.width, 0);
                  context.scale(-1, 1);
                  context.drawImage(video, sourceX, sourceY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);
                  canvas.toBlob((blob) => {
                    if (!blob) return setCameraError("Unable to create the profile photo.");
                    setCameraPreview(URL.createObjectURL(blob));
                  }, "image/jpeg", 0.9);
                }}
              ><i className="ti ti-camera" /> Take Photo</button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
